/**
 * Eva User Detail deep-link route (SF-3735).
 *
 * Hosts the reusable UserDetailDrawer on its own route so a user's Eva history
 * is directly link-able (AC5). The target user is taken from the `userId` query
 * param (e.g. `/eva/users?userId=abc&name=Jane&company=Acme`), so the drawer
 * opens pre-loaded when the URL is visited. Closing the drawer returns to the
 * Eva overview. Access is gated by `admin:eva:usage:read` via the access-control
 * provider, mirroring the overview page; unauthorized users see a 403 and no
 * request is issued.
 *
 * The optional `name` / `company` params only feed the header labels — the
 * canonical data is always fetched from the series endpoint by `userId`.
 */
import { useCan } from '@refinedev/core';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Result, Spin } from 'antd';

import { UserDetailDrawer } from './UserDetailDrawer';

/** Query param carrying the target user id for the deep link. */
export const USER_DETAIL_PARAM = 'userId';

export const EvaUserDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const userId = searchParams.get(USER_DETAIL_PARAM);
  const userName = searchParams.get('name');
  const companyName = searchParams.get('company');

  // Resolve access before issuing any request so unauthorized users never
  // trigger a GET (mirrors the overview page pattern).
  const { data: canAccess, isLoading: canLoading } = useCan({
    resource: 'eva',
    action: 'show',
  });
  const enabled = canAccess?.can === true;

  if (canLoading) {
    return <Spin style={{ display: 'flex', justifyContent: 'center', marginTop: 100 }} />;
  }

  if (!enabled) {
    return (
      <Result
        status="403"
        title="Not Authorized"
        subTitle={canAccess?.reason ?? 'You do not have permission to view Eva usage.'}
      />
    );
  }

  return (
    <UserDetailDrawer
      open
      onClose={() => navigate('/eva')}
      userId={userId}
      userName={userName}
      companyName={companyName}
      enabled={enabled}
    />
  );
};
